import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import OptimizedCard from "../components/teamcard";
import background from "../assets/background.webp";

// Styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import Igor from "../assets/team/igor.webp";
import Gevorg from "../assets/team/gevorg.webp";
import Suren from "../assets/team/suro.webp";
import Narek from "../assets/team/narek-h.webp";
import Levon from "../assets/team/levon.webp";
import Davit from "../assets/team/davit.webp";
import Garush from "../assets/team/garush.webp";
import Seda from "../assets/team/seda.webp";
import Hasmik from "../assets/team/hyasmik.webp";
import Ninel from "../assets/team/ninel.webp";
import Sofia from "../assets/team/sofia.webp";
import Karen_Ghazaryan from "../assets/team/karen_ghazaryan.webp";
import Telman_Hovhannisyan from "../assets/team/telman.webp";
import Karen_Eghiazaryan from "../assets/team/karen.webp";
import Argen_Tumanyan from "../assets/team/argen.webp";
import Hovhannes from "../assets/team/hovo.webp";
import Tamara from "../assets/team/tamara.webp";

const TeamSection = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const swiperRef = useRef(null);

  // Статичный список
  const teamList = useMemo(() => [
    { id: "igor", img: Igor },
    { id: "gevorg", img: Gevorg },
    { id: "suren", img: Suren },
    { id: "narek", img: Narek },
    { id: "levon", img: Levon },
    { id: "davit", img: Davit },
    { id: "garush", img: Garush },
    { id: "seda", img: Seda },
    { id: "hasmik", img: Hasmik },
    { id: "ninel", img: Ninel },
    { id: "sofia", img: Sofia },
    { id: "karen_gh", img: Karen_Ghazaryan },
    { id: "telman", img: Telman_Hovhannisyan },
    { id: "karen_eg", img: Karen_Eghiazaryan },
    { id: "argen", img: Argen_Tumanyan },
    { id: "hovo", img: Hovhannes },
    { id: "tamara", img: Tamara },
  ], []);

  // Рендер всех карточек сразу (один раз при загрузке)
  const allCards = useMemo(() => {
    return teamList.map((member, i) => (
      <SwiperSlide key={member.id} className="transform-gpu">
        <div className="px-2 py-4">
          <OptimizedCard
            index={i}
            item={{
              img: member.img,
              name: t(`team_members.${i}.name`),
              work: t(`team_members.${i}.work`),
            }}
          />
        </div>
      </SwiperSlide>
    ));
  }, [teamList, t]);

  useEffect(() => {
    // Анимация появления
    const timeout = setTimeout(() => setShow(true), 100);
    
    // Инициализация Swiper после монтирования
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.update();
    }
    
    return () => clearTimeout(timeout);
  }, []);

  return (
    <section className="relative w-full py-16 md:py-36 bg-slate-800 overflow-hidden shadow-inner">
      {/* Фон */}
      <div 
        className="absolute inset-0 z-0 transform-gpu pointer-events-none"
        style={{ 
          backgroundImage: `url(${background})`, 
          backgroundPosition: "center",
          willChange: "opacity" 
        }}
      />

      {/* Заголовок */}
      <div className={`relative z-10 text-center mb-10 md:mb-16 transition-all duration-1000 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <h1 className="font-['Cormorant_Garamond'] text-white uppercase text-[32px] md:text-[64px] tracking-[0.15em]">
          {t("team_title")}
        </h1>
        <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4 mb-2" />
        <p className="text-yellow-500 text-xs md:text-sm tracking-[0.5em] uppercase font-light">
          {t("team_subtitle")}
        </p>
      </div>

      {/* Swiper Слайдер */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-14">
        <Swiper
          ref={swiperRef}
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={20}
          breakpoints={{
            320: { slidesPerView: 1.2, spaceBetween: 15 },
            480: { slidesPerView: 2, spaceBetween: 15 },
            768: { slidesPerView: 2.5, spaceBetween: 20 },
            1024: { slidesPerView: 3, spaceBetween: 20 },
            1280: { slidesPerView: 4, spaceBetween: 25 },
          }}
          navigation={{
            nextEl: ".custom-next",
            prevEl: ".custom-prev",
          }}
          pagination={{
            clickable: true,
            el: '.custom-pagination',
            dynamicBullets: true,
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={teamList.length > 4}
          speed={800}
          grabCursor={true}
          preloadImages={true} // Загружаем все изображения сразу
          watchSlidesProgress={false} // Отключаем для производительности
          className="team-swiper !pb-16"
        >
          {allCards}
        </Swiper>

        {/* Кнопка назад */}
        <button className="custom-prev absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 hover:bg-yellow-500/70 rounded-full flex items-center justify-center transition-all group max-md:hidden cursor-pointer">
          <svg className="w-5 h-5 text-yellow-500 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Кнопка вперед */}
        <button className="custom-next absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 hover:bg-yellow-500/70 rounded-full flex items-center justify-center transition-all group max-md:hidden cursor-pointer">
          <svg className="w-5 h-5 text-yellow-500 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Пагинация */}
        <div className="custom-pagination flex justify-center gap-2 mt-6" />
      </div>

      {/* Стили */}
      <style jsx global>{`
        .team-swiper {
          overflow: visible !important;
          padding-bottom: 50px !important;
        }
        
        .team-swiper .swiper-wrapper {
          will-change: transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .team-swiper .swiper-slide {
          height: auto !important;
          transition: transform 0.3s ease;
        }
        
        /* Пагинация */
        .custom-pagination {
          position: relative;
          bottom: 0;
          margin-top: 20px;
        }
        
        .custom-pagination .swiper-pagination-bullet {
          background: #eab308 !important;
          opacity: 0.4;
          width: 8px;
          height: 8px;
          transition: all 0.3s ease;
        }
        
        .custom-pagination .swiper-pagination-bullet-active {
          opacity: 1;
          width: 24px;
          border-radius: 4px;
        }
        
        /* Кнопки навигации */
        .custom-prev,
        .custom-next {
          pointer-events: auto !important;
          cursor: pointer !important;
        }
      
        
        /* Для мобильных */
        @media (max-width: 768px) {
          .custom-prev,
          .custom-next {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default TeamSection;